import { useState, useRef, useEffect, useCallback, useMemo, Fragment as Fragment$1 } from 'react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { createPortal } from 'react-dom';

// src/CopyLinkButton.tsx
function defaultWriteToClipboard(text) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText !== void 0) {
    return navigator.clipboard.writeText(text);
  }
  return Promise.reject(new Error("clipboard unavailable"));
}
function LinkGlyph() {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: "vdiff__copy-icon",
      width: "14",
      height: "14",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true",
      focusable: "false",
      children: [
        /* @__PURE__ */ jsx("path", { d: "M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" }),
        /* @__PURE__ */ jsx("path", { d: "M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" })
      ]
    }
  );
}
function CopyLinkButton(props) {
  const {
    value,
    writeToClipboard = defaultWriteToClipboard,
    onCopied,
    idleLabel = "Copy link",
    copiedLabel = "Copied",
    confirmMs = 1500,
    className,
    testId = "copy-link",
    ariaLabel,
    disabled = false
  } = props;
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(void 0);
  const valueRef = useRef(value);
  const writeRef = useRef(writeToClipboard);
  const onCopiedRef = useRef(onCopied);
  const confirmMsRef = useRef(confirmMs);
  valueRef.current = value;
  writeRef.current = writeToClipboard;
  onCopiedRef.current = onCopied;
  confirmMsRef.current = confirmMs;
  useEffect(
    () => () => {
      if (timerRef.current !== void 0) clearTimeout(timerRef.current);
    },
    []
  );
  const onClick = useCallback(() => {
    const text = valueRef.current;
    writeRef.current(text).then(() => {
      setCopied(true);
      onCopiedRef.current?.(text);
      if (timerRef.current !== void 0) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), confirmMsRef.current);
    }).catch(() => {
    });
  }, []);
  const label = copied ? copiedLabel : idleLabel;
  const classes = ["vdiff__btn", "vdiff__copy-link"];
  if (copied) classes.push("vdiff__copy-link--copied");
  if (className) classes.push(className);
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      className: classes.join(" "),
      "data-testid": testId,
      "data-copied": copied ? "true" : void 0,
      "aria-label": ariaLabel ?? label,
      "aria-live": "polite",
      disabled,
      onClick,
      children: [
        /* @__PURE__ */ jsx(LinkGlyph, {}),
        /* @__PURE__ */ jsx("span", { className: "vdiff__copy-label", children: label })
      ]
    }
  );
}
function defaultFormatDate(iso) {
  return /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : iso;
}
function createdAtOf(versions, id) {
  if (id === void 0) return void 0;
  const v = versions.find((x) => x.versionId === id);
  if (v === void 0) return void 0;
  const t = Date.parse(v.createdAt);
  return Number.isNaN(t) ? void 0 : t;
}
function VersionSwitcher(props) {
  const {
    versions,
    valueA,
    valueB,
    onCompare,
    onSwapVersions,
    formatDate = defaultFormatDate,
    renderPicker,
    leftTestId = "vdiff-version-switcher-left",
    rightTestId = "vdiff-version-switcher-right",
    swapTestId = "vdiff-swap-versions"
  } = props;
  if (versions.length === 0) return null;
  const formatOption = (version) => {
    const parts = [version.label, formatDate(version.createdAt)];
    if (version.author !== void 0 && version.author !== "") parts.push(version.author);
    const base = parts.join(" \xB7 ");
    return version.isHead === true ? `${base} (head)` : base;
  };
  const equal = valueA !== void 0 && valueA === valueB;
  const aTime = createdAtOf(versions, valueA);
  const bTime = createdAtOf(versions, valueB);
  const reverse = aTime !== void 0 && bTime !== void 0 && aTime > bTime;
  const selectA = (id) => {
    if (onCompare !== void 0 && id !== valueB) onCompare(id, valueB ?? id);
  };
  const selectB = (id) => {
    if (onCompare !== void 0 && id !== valueA) onCompare(valueA ?? id, id);
  };
  const onSwap = () => {
    if (equal) return;
    if (onSwapVersions !== void 0) onSwapVersions();
    else if (onCompare !== void 0 && valueA !== void 0 && valueB !== void 0) {
      onCompare(valueB, valueA);
    }
  };
  const renderSide = (side) => {
    const value = side === "left" ? valueA : valueB;
    const onSelect = side === "left" ? selectA : selectB;
    const testId = side === "left" ? leftTestId : rightTestId;
    if (renderPicker !== void 0) {
      return renderPicker({ side, versions, value, onSelect, formatOption, testId });
    }
    return /* @__PURE__ */ jsx(
      "select",
      {
        className: "vdiff__version-select",
        "data-testid": testId,
        "data-side": side,
        "aria-label": side === "left" ? "Base version (left)" : "Compare version (right)",
        value: value ?? "",
        onChange: (event) => onSelect(event.target.value),
        children: versions.map((v) => /* @__PURE__ */ jsx("option", { value: v.versionId, children: formatOption(v) }, v.versionId))
      }
    );
  };
  return /* @__PURE__ */ jsxs("div", { className: "vdiff__switcher", "data-testid": "vdiff-version-switcher", children: [
    /* @__PURE__ */ jsx("span", { className: "vdiff__version-slot vdiff__version-slot--a", children: renderSide("left") }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "vdiff__btn vdiff__swap",
        "data-testid": swapTestId,
        "aria-label": "Swap base and compare versions",
        disabled: equal,
        onClick: onSwap,
        children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\u21C4" })
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "vdiff__version-slot vdiff__version-slot--b", children: renderSide("right") }),
    equal ? /* @__PURE__ */ jsx("span", { className: "vdiff__pair-hint", "data-testid": "vdiff-pair-hint", "aria-live": "polite", children: "Pick two different versions" }) : reverse ? /* @__PURE__ */ jsx(
      "span",
      {
        className: "vdiff__pair-hint vdiff__pair-hint--reverse",
        "data-testid": "vdiff-pair-hint",
        "aria-live": "polite",
        children: "reverse (base is newer)"
      }
    ) : null
  ] });
}
function DiffHeader(props) {
  const {
    model,
    onPrev,
    onNext,
    onClose,
    versions,
    valueA,
    valueB,
    onCompare,
    onSwapVersions,
    formatDate,
    renderVersionPicker,
    permalink,
    onCopyLink,
    writeToClipboard
  } = props;
  const showSwitcher = versions !== void 0 && versions.length > 0;
  return /* @__PURE__ */ jsxs("header", { className: "vdiff__header", children: [
    /* @__PURE__ */ jsxs("div", { className: "vdiff__title", children: [
      /* @__PURE__ */ jsx("span", { className: "vdiff__kind", "data-kind": model.resourceKind, children: model.resourceKind }),
      showSwitcher ? /* @__PURE__ */ jsx(
        VersionSwitcher,
        {
          versions,
          valueA,
          valueB,
          onCompare,
          onSwapVersions,
          formatDate,
          renderPicker: renderVersionPicker
        }
      ) : /* @__PURE__ */ jsxs("span", { className: "vdiff__labels", children: [
        /* @__PURE__ */ jsx("span", { className: "vdiff__label vdiff__label--a", children: model.aLabel }),
        /* @__PURE__ */ jsx("span", { className: "vdiff__arrow", "aria-hidden": "true", children: "\u2192" }),
        /* @__PURE__ */ jsx("span", { className: "vdiff__label vdiff__label--b", children: model.bLabel })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "vdiff__stats", children: [
      /* @__PURE__ */ jsxs("span", { className: "vdiff__stat", "data-kind": "added", children: [
        model.stats.added,
        " added"
      ] }),
      /* @__PURE__ */ jsxs("span", { className: "vdiff__stat", "data-kind": "removed", children: [
        model.stats.removed,
        " removed"
      ] }),
      /* @__PURE__ */ jsxs("span", { className: "vdiff__stat", "data-kind": "modified", children: [
        model.stats.modified,
        " modified"
      ] }),
      /* @__PURE__ */ jsxs("span", { className: "vdiff__stat", "data-kind": "moved", children: [
        model.stats.moved,
        " moved"
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "vdiff__nav", children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "vdiff__btn",
          "data-testid": "diff-nav-prev",
          onClick: onPrev,
          "aria-label": "Previous change",
          children: "Prev"
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "vdiff__btn",
          "data-testid": "diff-nav-next",
          onClick: onNext,
          "aria-label": "Next change",
          children: "Next"
        }
      ),
      permalink !== void 0 ? /* @__PURE__ */ jsx(
        CopyLinkButton,
        {
          value: permalink,
          testId: "vdiff-copy-link",
          ariaLabel: "Copy link to this diff",
          onCopied: onCopyLink,
          writeToClipboard
        }
      ) : null,
      onClose !== void 0 ? /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "vdiff__btn vdiff__btn--close",
          "data-testid": "diff-close",
          onClick: onClose,
          "aria-label": "Close diff",
          children: "Close"
        }
      ) : null
    ] })
  ] });
}

// src/diff-bridge.ts
var OP_TO_STATUS = {
  insert: "added",
  delete: "removed",
  move: "moved",
  change: "modified"
};
function classifyChange(edits) {
  if (edits === void 0) return "modified";
  let ins = 0;
  let del = 0;
  for (const e of edits) {
    if (e.op === "insert") ins += 1;
    else if (e.op === "delete") del += 1;
  }
  if (ins > 0 && del === 0) return "added";
  if (del > 0 && ins === 0) return "removed";
  return "modified";
}
function documentDiffToVersionDiff(diff, opts) {
  const hunks = [];
  for (const op of diff.ops) {
    if (op.op === "equal") continue;
    const hunk = { status: OP_TO_STATUS[op.op] };
    if (op.oldIndex !== void 0) hunk.blockRefA = String(op.oldIndex);
    if (op.newIndex !== void 0) hunk.blockRefB = String(op.newIndex);
    if (op.oldBlockHtml !== void 0) hunk.aHtml = op.oldBlockHtml;
    if (op.newBlockHtml !== void 0) hunk.bHtml = op.newBlockHtml;
    if (op.text?.edits !== void 0) hunk.inline = op.text.edits;
    if (op.changeHash !== void 0) hunk.changeHash = op.changeHash;
    hunks.push(hunk);
  }
  const counts = diff.counts;
  let added = counts.inserted ?? 0;
  let removed = counts.deleted ?? 0;
  let modified = counts.changed ?? 0;
  const moved = counts.moved ?? 0;
  for (const op of diff.ops) {
    if (op.op !== "change") continue;
    const cls = classifyChange(op.text?.edits);
    if (cls === "added") {
      added += 1;
      modified -= 1;
    } else if (cls === "removed") {
      removed += 1;
      modified -= 1;
    }
  }
  return {
    resourceKind: opts.resourceKind,
    granularity: opts.granularity ?? "block",
    aLabel: opts.aLabel,
    bLabel: opts.bLabel,
    hunks,
    stats: { added, removed, modified, moved }
  };
}
function lineDiffToVersionDiff(diff, opts) {
  return documentDiffToVersionDiff(diff, { ...opts, granularity: "line" });
}

// src/diffLink.ts
function encodeUtf8ToBase64Url(text) {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function decodeBase64UrlToUtf8(token) {
  const normalized = token.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - normalized.length % 4) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}
function serializeDiffLink(token) {
  const parts = [token.scope, token.aVersionId, token.bVersionId];
  if (token.changeHash !== void 0 && token.changeHash !== "") {
    parts.push(token.changeHash);
  }
  return encodeUtf8ToBase64Url(JSON.stringify(parts));
}
function parseDiffLink(token) {
  if (token === "") return null;
  let decoded;
  try {
    decoded = decodeBase64UrlToUtf8(token);
  } catch {
    return null;
  }
  let payload;
  try {
    payload = JSON.parse(decoded);
  } catch {
    return null;
  }
  if (!Array.isArray(payload) || payload.length < 3) return null;
  const [scope, aVersionId, bVersionId, changeHash] = payload;
  if (typeof scope !== "string" || typeof aVersionId !== "string" || typeof bVersionId !== "string") {
    return null;
  }
  const result = { scope, aVersionId, bVersionId };
  if (typeof changeHash === "string" && changeHash !== "") {
    result.changeHash = changeHash;
  }
  return result;
}
function toSearchParams(search) {
  return typeof search === "string" ? new URLSearchParams(search) : search;
}
function readDiffToken(search, param = "diff") {
  const value = toSearchParams(search).get(param);
  return value === null ? null : parseDiffLink(value);
}
function writeDiffToken(search, token, param = "diff") {
  const params = new URLSearchParams(toSearchParams(search));
  params.set(param, serializeDiffLink(token));
  return params.toString();
}
function syncDiffTokenToLocation(token, param = "diff") {
  if (typeof window === "undefined" || typeof window.history?.replaceState !== "function") {
    return "";
  }
  const nextSearch = writeDiffToken(window.location.search, token, param);
  const url = `${window.location.pathname}?${nextSearch}${window.location.hash}`;
  window.history.replaceState(window.history.state, "", url);
  return nextSearch;
}
function buildDiffPermalink(origin, pathname, token, param = "diff") {
  const search = writeDiffToken("", token, param);
  return `${origin}${pathname}?${search}`;
}
var DEFAULT_PILL_OPTIONS = [
  { value: "brief", label: "Brief" },
  { value: "research", label: "Research" },
  { value: "article", label: "Article" }
];
function PillToggleGroup(props) {
  const {
    value,
    onChange,
    options = DEFAULT_PILL_OPTIONS,
    ariaLabel,
    className,
    testId = "pill-toggle-group"
  } = props;
  const pressed = new Set(value);
  const rootClassName = className ? `nk-review-pills ${className}` : "nk-review-pills";
  return (
    // biome-ignore lint/a11y/useSemanticElements: intentional ARIA group of independent toggle pills — a <fieldset> would impose form/legend semantics this stage-visibility toolbar does not want.
    /* @__PURE__ */ jsx("div", { className: rootClassName, role: "group", "aria-label": ariaLabel, "data-testid": testId, children: options.map((option) => {
      const isPressed = pressed.has(option.value);
      return /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "nk-review-pills__pill",
          "aria-pressed": isPressed,
          disabled: option.disabled,
          "data-kind": option.value,
          "data-testid": `pill-${option.value}`,
          onClick: () => {
            if (option.disabled) {
              return;
            }
            const next = isPressed ? value.filter((kind) => kind !== option.value) : [...value, option.value];
            onChange?.(next);
          },
          children: option.label
        },
        option.value
      );
    }) })
  );
}
function buildGroups(hunks) {
  const groups = [];
  hunks.forEach((hunk, index) => {
    const sectionId = hunk.sectionId;
    const last = groups.at(-1);
    if (sectionId !== void 0 && last !== void 0 && last.sectionId === sectionId) {
      last.items.push({ hunk, index });
    } else {
      groups.push({ sectionId, items: [{ hunk, index }] });
    }
  });
  return groups;
}
function isPresent(status, mode) {
  if (mode === "unified") return true;
  if (status === "added") return mode === "b";
  if (status === "removed") return mode === "a";
  return true;
}
function pickHtml(hunk, mode) {
  if (mode === "a") return hunk.aHtml;
  if (mode === "b") return hunk.bHtml;
  return hunk.bHtml ?? hunk.aHtml;
}
function renderInlineSpans(spans, mode) {
  return spans.map((span, i) => {
    const key = `${i}-${span.op}`;
    if (span.op === "equal") {
      return /* @__PURE__ */ jsx("span", { children: span.text }, key);
    }
    if (span.op === "delete") {
      if (mode === "b") return null;
      return /* @__PURE__ */ jsx("span", { className: "diff-del", children: span.text }, key);
    }
    if (mode === "a") return null;
    return /* @__PURE__ */ jsx("span", { className: "diff-ins", children: span.text }, key);
  });
}
function renderCellContent(hunk, mode, granularity) {
  if (hunk.inline !== void 0) {
    return /* @__PURE__ */ jsx("div", { className: "vdiff__line", children: renderInlineSpans(hunk.inline, mode) });
  }
  if (granularity === "block") {
    const html2 = pickHtml(hunk, mode);
    if (html2 !== void 0) {
      return /* @__PURE__ */ jsx("div", { className: "vdiff__block", dangerouslySetInnerHTML: { __html: html2 } });
    }
    if (hunk.inline !== void 0) {
      return /* @__PURE__ */ jsx("div", { className: "vdiff__line", children: renderInlineSpans(hunk.inline, mode) });
    }
    return null;
  }
  if (hunk.inline !== void 0) {
    return /* @__PURE__ */ jsx("div", { className: "vdiff__line", children: renderInlineSpans(hunk.inline, mode) });
  }
  const html = pickHtml(hunk, mode);
  if (html !== void 0) return /* @__PURE__ */ jsx("div", { className: "vdiff__line", children: html });
  return null;
}
function scrollCenter(el) {
  if (el != null && typeof el.scrollIntoView === "function") {
    el.scrollIntoView({ block: "center" });
  }
}
function anchorBloomKey(anchor) {
  return anchor.replyId !== void 0 ? `${anchor.commentId}:${anchor.replyId}` : anchor.commentId;
}
function groupNotesByChangeHash(anchors) {
  const map = /* @__PURE__ */ new Map();
  if (anchors === void 0) return map;
  for (const anchor of anchors) {
    const list = map.get(anchor.changeHash);
    if (list === void 0) map.set(anchor.changeHash, [anchor]);
    else list.push(anchor);
  }
  return map;
}
function SideBySideDiff(props) {
  const {
    model,
    inline = false,
    onHunkSelect,
    onClose,
    className,
    testId = "review-diff",
    versions,
    valueA,
    valueB,
    onCompare,
    onSwapVersions,
    formatDate,
    renderVersionPicker,
    loading = false,
    permalink,
    onCopyLink,
    writeToClipboard,
    revealChangeHash,
    onReady,
    noteAnchors,
    onNoteAnchorClick,
    revealCommentId,
    revealReplyId
  } = props;
  const [active, setActive] = useState(0);
  const [collapsed, setCollapsed] = useState(() => /* @__PURE__ */ new Set());
  const [bloomHunk, setBloomHunk] = useState(null);
  const [bloomAnchor, setBloomAnchor] = useState(null);
  const notesByChange = useMemo(() => groupNotesByChangeHash(noteAnchors), [noteAnchors]);
  const rootRef = useRef(null);
  const hunkRefs = useRef([]);
  const didMount = useRef(false);
  const activeRef = useRef(0);
  const hunksRef = useRef(model.hunks);
  const onHunkSelectRef = useRef(onHunkSelect);
  activeRef.current = active;
  hunksRef.current = model.hunks;
  onHunkSelectRef.current = onHunkSelect;
  const groups = useMemo(() => buildGroups(model.hunks), [model.hunks]);
  const navTo = useCallback((delta) => {
    const total = hunksRef.current.length;
    if (total === 0) return;
    const target = Math.max(0, Math.min(total - 1, activeRef.current + delta));
    const sid = hunksRef.current[target]?.sectionId;
    if (sid !== void 0) {
      setCollapsed((prev) => {
        if (!prev.has(sid)) return prev;
        const next = new Set(prev);
        next.delete(sid);
        return next;
      });
    }
    setActive(target);
  }, []);
  const toggleSection = useCallback((sectionId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);
  const onKeyDown = useCallback(
    (event) => {
      const target = event.target;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) return;
      if (event.key === "n" || event.key === "N") {
        event.preventDefault();
        navTo(1);
      } else if (event.key === "p" || event.key === "P") {
        event.preventDefault();
        navTo(-1);
      } else if (event.key === "Escape" && onClose !== void 0) {
        event.preventDefault();
        onClose();
      }
    },
    [navTo, onClose]
  );
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    const hunk = hunksRef.current[active];
    if (hunk !== void 0) onHunkSelectRef.current?.(hunk, active);
    scrollCenter(hunkRefs.current[active]);
  }, [active]);
  useEffect(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    onReadyRef.current?.();
  }, []);
  useEffect(() => {
    const hunks = model.hunks;
    let targetIndex = -1;
    let anchorKey = null;
    if (revealChangeHash !== void 0 && revealChangeHash !== "") {
      targetIndex = hunks.findIndex((h) => h.changeHash === revealChangeHash);
    } else if (revealCommentId !== void 0 && revealCommentId !== "") {
      const anchor = (noteAnchors ?? []).find(
        (a) => a.commentId === revealCommentId && (revealReplyId === void 0 || a.replyId === revealReplyId)
      );
      if (anchor !== void 0) {
        targetIndex = hunks.findIndex((h) => h.changeHash === anchor.changeHash);
        anchorKey = anchorBloomKey(anchor);
      }
    }
    if (targetIndex < 0) return void 0;
    setActive(targetIndex);
    setBloomHunk(targetIndex);
    setBloomAnchor(anchorKey);
    scrollCenter(hunkRefs.current[targetIndex]);
    const timer = setTimeout(() => {
      setBloomHunk(null);
      setBloomAnchor(null);
    }, 1600);
    return () => clearTimeout(timer);
  }, [revealChangeHash, revealCommentId, revealReplyId, model.hunks, noteAnchors]);
  const renderNoteAnchors = (hunk) => {
    const changeHash = hunk.changeHash;
    if (changeHash === void 0) return null;
    const anchors = notesByChange.get(changeHash);
    if (anchors === void 0 || anchors.length === 0) return null;
    return /* @__PURE__ */ jsx("span", { className: "vdiff__notes", "data-testid": `vdiff-notes-${changeHash}`, children: anchors.map((anchor) => {
      const key = anchorBloomKey(anchor);
      const classes = ["vdiff__note-anchor"];
      if (bloomAnchor === key) classes.push("vdiff__note-anchor--bloom");
      return /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          className: classes.join(" "),
          "data-testid": `vdiff-note-anchor-${anchor.commentId}`,
          "data-change-hash": changeHash,
          "data-comment-id": anchor.commentId,
          "data-reply-id": anchor.replyId,
          "aria-label": anchor.label ?? `Open note thread (${anchor.count ?? 1})`,
          onClick: () => onNoteAnchorClick?.(anchor),
          children: [
            /* @__PURE__ */ jsx(
              "svg",
              {
                className: "vdiff__note-icon",
                width: "12",
                height: "12",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                strokeWidth: "2",
                strokeLinecap: "round",
                strokeLinejoin: "round",
                "aria-hidden": "true",
                focusable: "false",
                children: /* @__PURE__ */ jsx("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" })
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "vdiff__note-count", children: anchor.count ?? 1 })
          ]
        },
        key
      );
    }) });
  };
  const renderCell = (hunk, index, mode) => {
    const present = isPresent(hunk.status, mode);
    const isActive = index === active;
    const classes = ["vdiff__hunk"];
    if (!present) classes.push("vdiff__hunk--ghost");
    if (isActive) classes.push("vdiff__hunk--active");
    if (bloomHunk === index) classes.push("vdiff__hunk--bloom");
    const testIdForCell = mode === "b" ? `diff-hunk-b-${index}` : `diff-hunk-${index}`;
    const captureRef = mode !== "b" ? (el) => {
      hunkRefs.current[index] = el;
    } : void 0;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref: captureRef,
        className: classes.join(" "),
        "data-status": hunk.status,
        "data-side": mode,
        "data-active": isActive ? "true" : void 0,
        "data-change-hash": hunk.changeHash,
        "aria-current": isActive ? "true" : void 0,
        "data-testid": testIdForCell,
        children: [
          present ? renderCellContent(hunk, mode, model.granularity) : /* @__PURE__ */ jsx("span", { className: "vdiff__ghost-fill", "aria-hidden": "true" }),
          mode === "unified" ? renderNoteAnchors(hunk) : null
        ]
      },
      `cell-${mode}-${index}`
    );
  };
  const renderConnector = (hunk, index) => /* @__PURE__ */ jsxs(
    "div",
    {
      className: "vdiff__connector",
      "data-status": hunk.status,
      "data-testid": `diff-connector-${index}`,
      children: [
        /* @__PURE__ */ jsx("span", { className: "vdiff__connector-bar", "aria-hidden": "true" }),
        renderNoteAnchors(hunk)
      ]
    },
    `conn-${index}`
  );
  const renderSectionHeader = (sid, gCollapsed) => /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      className: "vdiff__section-header",
      "data-testid": `diff-section-${sid}`,
      "aria-expanded": !gCollapsed,
      onClick: () => toggleSection(sid),
      children: [
        /* @__PURE__ */ jsx("span", { className: "vdiff__section-caret", "aria-hidden": "true" }),
        sid
      ]
    },
    `section-${sid}`
  );
  const renderRows = () => groups.map((group) => {
    const sid = group.sectionId;
    const gCollapsed = sid !== void 0 && collapsed.has(sid);
    const firstIndex = group.items[0]?.index ?? -1;
    return /* @__PURE__ */ jsxs(Fragment$1, { children: [
      sid !== void 0 ? renderSectionHeader(sid, gCollapsed) : null,
      gCollapsed ? null : group.items.flatMap(
        (it) => inline ? [renderCell(it.hunk, it.index, "unified")] : [
          renderCell(it.hunk, it.index, "a"),
          renderConnector(it.hunk, it.index),
          renderCell(it.hunk, it.index, "b")
        ]
      )
    ] }, `group-${sid ?? "loose"}-${firstIndex}`);
  });
  const rootClasses = ["vdiff"];
  if (inline) rootClasses.push("vdiff--inline");
  if (className) rootClasses.push(className);
  const pressedBackdrop = useRef(false);
  const onOverlayMouseDown = (event) => {
    pressedBackdrop.current = event.target === event.currentTarget;
  };
  const onOverlayClick = (event) => {
    if (onClose !== void 0 && pressedBackdrop.current && event.target === event.currentTarget) {
      onClose();
    }
  };
  const dialog = (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop is a decorative dismiss target; keyboard Escape (on the dialog) is the a11y path.
    // biome-ignore lint/a11y/noStaticElementInteractions: decorative modal backdrop; the interactive surface is the role="dialog" panel it wraps.
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "vdiff-overlay",
        "data-testid": "review-diff-overlay",
        onMouseDown: onOverlayMouseDown,
        onClick: onOverlayClick,
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            ref: rootRef,
            className: rootClasses.join(" "),
            role: "dialog",
            "aria-modal": "true",
            "aria-label": `Diff ${model.aLabel} to ${model.bLabel}`,
            "data-testid": testId,
            tabIndex: -1,
            onKeyDown,
            children: [
              /* @__PURE__ */ jsx(
                DiffHeader,
                {
                  model,
                  onPrev: () => navTo(-1),
                  onNext: () => navTo(1),
                  onClose,
                  versions,
                  valueA,
                  valueB,
                  onCompare,
                  onSwapVersions,
                  formatDate,
                  renderVersionPicker,
                  permalink,
                  onCopyLink,
                  writeToClipboard
                }
              ),
              model.hunks.length === 0 ? /* @__PURE__ */ jsx("div", { className: "vdiff__empty", "data-testid": "diff-empty", children: "No changes" }) : /* @__PURE__ */ jsxs("div", { className: "vdiff__scroll", "data-testid": "diff-scroll", children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: inline ? "vdiff__grid vdiff__grid--inline" : "vdiff__grid",
                    "data-testid": "diff-grid",
                    children: renderRows()
                  }
                ),
                loading ? /* @__PURE__ */ jsxs("div", { className: "vdiff__loading", "data-testid": "diff-loading", "aria-live": "polite", children: [
                  /* @__PURE__ */ jsx("span", { className: "vdiff__spinner", "aria-hidden": "true" }),
                  /* @__PURE__ */ jsx("span", { className: "vdiff__loading-label", children: "Comparing\u2026" })
                ] }) : null
              ] })
            ]
          }
        )
      }
    )
  );
  return typeof document !== "undefined" ? createPortal(dialog, document.body) : dialog;
}

// src/stageColumns.ts
var ORDER = ["brief", "article", "research"];
var DEFAULT_GUTTER_TRACK = "340px";
var DEFAULT_PANE_TRACK = "820px";
function stageColumns(shown, options = {}) {
  const gutterTrack = options.gutterTrack ?? DEFAULT_GUTTER_TRACK;
  const paneTrack = options.paneTrack ?? DEFAULT_PANE_TRACK;
  const wanted = new Set(shown);
  const panes = ORDER.filter((kind) => wanted.has(kind));
  if (panes.length === 0) {
    return { gridTemplateColumns: "", slots: [] };
  }
  const slots = [];
  const tracks = [];
  if (panes.length === 1) {
    const only = panes[0];
    if (only) {
      slots.push(`pane:${only}`, `gutter:solo:${only}`);
      tracks.push(paneTrack, gutterTrack);
    }
    return { gridTemplateColumns: tracks.join(" "), slots };
  }
  panes.forEach((kind, index) => {
    const prev = panes[index - 1];
    if (prev !== void 0) {
      slots.push(`gutter:${prev}-${kind}`);
      tracks.push(gutterTrack);
    }
    slots.push(`pane:${kind}`);
    tracks.push(paneTrack);
  });
  return { gridTemplateColumns: tracks.join(" "), slots };
}
function refusalFor(action, tick) {
  const reason = action.disabledReason?.(tick) ?? null;
  return reason && reason.length > 0 ? reason : null;
}
function TimelineTickMenu({ tick, actions, anchor, onClose }) {
  const itemRefs = useRef([]);
  const menuRef = useRef(null);
  const enabled = useMemo(
    () => actions.map((a, i) => refusalFor(a, tick) ? -1 : i).filter((i) => i >= 0),
    [actions, tick]
  );
  useEffect(() => {
    const first = enabled[0];
    if (first !== void 0) {
      itemRefs.current[first]?.focus();
      return;
    }
    menuRef.current?.focus();
  }, []);
  useEffect(() => {
    const onDown = (event) => {
      const target = event.target;
      for (const el of itemRefs.current) {
        if (el && target && el.parentElement?.contains(target)) return;
      }
      onClose(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);
  const focusAt = (position) => {
    if (enabled.length === 0) return;
    const clamped = Math.max(0, Math.min(position, enabled.length - 1));
    const index = enabled[clamped];
    if (index !== void 0) itemRefs.current[index]?.focus();
  };
  const positionOf = (index) => Math.max(0, enabled.indexOf(index));
  const activate = (action) => {
    if (refusalFor(action, tick)) return;
    onClose(false);
    action.onSelect(tick.versionId);
  };
  const onItemKeyDown = (event, index) => {
    const action = actions[index];
    if (!action) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusAt(positionOf(index) + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusAt(positionOf(index) - 1);
        break;
      case "Home":
        event.preventDefault();
        focusAt(0);
        break;
      case "End":
        event.preventDefault();
        focusAt(enabled.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        activate(action);
        break;
      case "Escape":
        event.preventDefault();
        onClose(true);
        break;
      case "Tab":
        onClose(false);
        break;
    }
  };
  const onMenuKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Escape") {
      event.preventDefault();
      onClose(true);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "Home") {
      event.preventDefault();
      focusAt(0);
    } else if (event.key === "ArrowUp" || event.key === "End") {
      event.preventDefault();
      focusAt(enabled.length - 1);
    }
  };
  const swallow = (event) => event.preventDefault();
  return createPortal(
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: the menu container owns the roving-focus key handling for its menuitem children (WAI-ARIA APG menu pattern).
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: menuRef,
        className: "nk-review-timeline__tick-menu",
        "data-testid": "tick-menu",
        role: "menu",
        "aria-label": `Actions for ${tick.label}`,
        tabIndex: -1,
        style: { position: "fixed", top: anchor.y, left: anchor.x },
        onKeyDown: onMenuKeyDown,
        onContextMenu: swallow,
        children: actions.map((action, index) => {
          const refusal = refusalFor(action, tick);
          return /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              role: "menuitem",
              ref: (el) => {
                itemRefs.current[index] = el;
              },
              "data-testid": `tick-menu-item-${action.id}`,
              className: [
                "nk-review-timeline__tick-menu-item",
                action.tone === "prominent" && "nk-review-timeline__tick-menu-item--prominent",
                refusal && "nk-review-timeline__tick-menu-item--disabled"
              ].filter(Boolean).join(" "),
              "aria-disabled": refusal ? "true" : void 0,
              tabIndex: -1,
              onClick: () => activate(action),
              onKeyDown: (event) => onItemKeyDown(event, index),
              children: [
                /* @__PURE__ */ jsx("span", { className: "nk-review-timeline__tick-menu-label", children: action.label }),
                refusal ? /* @__PURE__ */ jsx("span", { className: "nk-review-timeline__tick-menu-reason", children: refusal }) : null
              ]
            },
            action.id
          );
        })
      }
    ),
    document.body
  );
}
var TIMELINE_MIN_VERSIONS = 2;
function hasNavigableTimeline(versions) {
  return versions.length >= TIMELINE_MIN_VERSIONS;
}
function TimelineBar({
  versions,
  selectedA,
  selectedB,
  currentVersionId,
  onView,
  onSetA,
  onSetB,
  tickActions,
  className,
  testId
}) {
  const ordered = useMemo(() => [...versions].sort((a, b) => b.seq - a.seq), [versions]);
  const currentTickId = useMemo(() => {
    const named = versions.find((v) => v.versionId === currentVersionId);
    if (named) return named.versionId;
    return versions.find((v) => v.isHead)?.versionId;
  }, [versions, currentVersionId]);
  const itemRefs = useRef([]);
  const orderedRef = useRef(ordered);
  orderedRef.current = ordered;
  const [focusedIndex, setFocusedIndex] = useState(() => {
    const head = [...versions].sort((a, b) => b.seq - a.seq).findIndex((v) => v.isHead);
    return head >= 0 ? head : 0;
  });
  const lastIndex = Math.max(0, ordered.length - 1);
  const activeIndex = Math.min(focusedIndex, lastIndex);
  const hasActions = (tickActions?.length ?? 0) > 0;
  const [menu, setMenu] = useState(null);
  const openVersionIdRef = useRef(null);
  openVersionIdRef.current = menu?.versionId ?? null;
  const closeMenu = useCallback((restoreFocus) => {
    const openId = openVersionIdRef.current;
    setMenu(null);
    if (!restoreFocus || !openId) return;
    const index = orderedRef.current.findIndex((v) => v.versionId === openId);
    if (index >= 0) itemRefs.current[index]?.focus();
  }, []);
  const openMenu = (version, x, y) => {
    if (!hasActions) return;
    setMenu({ versionId: version.versionId, x, y });
  };
  const openMenuAtTick = (index, version) => {
    const rect = itemRefs.current[index]?.getBoundingClientRect();
    openMenu(version, (rect?.right ?? 0) + 4, rect?.top ?? 0);
  };
  const menuTick = menu ? ordered.find((v) => v.versionId === menu.versionId) : void 0;
  const moveFocus = (target) => {
    const clamped = Math.max(0, Math.min(target, lastIndex));
    setFocusedIndex(clamped);
    itemRefs.current[clamped]?.focus();
  };
  const handleClick = (event, index, version) => {
    setFocusedIndex(index);
    if (event.shiftKey) {
      onSetA?.(version.versionId);
    } else if (event.altKey) {
      onSetB?.(version.versionId);
    } else {
      onView?.(version.versionId);
    }
  };
  const handleKeyDown = (event, index, version) => {
    switch (event.key) {
      case "ArrowUp":
        event.preventDefault();
        moveFocus(index - 1);
        break;
      case "ArrowDown":
        event.preventDefault();
        moveFocus(index + 1);
        break;
      case "Home":
        event.preventDefault();
        moveFocus(0);
        break;
      case "End":
        event.preventDefault();
        moveFocus(lastIndex);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        onView?.(version.versionId);
        break;
      case "a":
      case "A":
        event.preventDefault();
        onSetA?.(version.versionId);
        break;
      case "b":
      case "B":
        event.preventDefault();
        onSetB?.(version.versionId);
        break;
      // THE KEYBOARD DOOR to the action menu. Both spellings, because the two
      // halves of the platform disagree: `Shift+F10` is the Windows/Linux
      // convention every screen reader teaches, `ContextMenu` is the dedicated
      // key when a keyboard has one. Supporting only `contextmenu` (the mouse
      // event) leaves every keyboard user with no route to these actions at
      // all — not a degraded route, none.
      case "ContextMenu":
        event.preventDefault();
        openMenuAtTick(index, version);
        break;
      case "F10":
        if (!event.shiftKey) break;
        event.preventDefault();
        openMenuAtTick(index, version);
        break;
    }
  };
  const handleContextMenu = (event, index, version) => {
    if (!hasActions) return;
    event.preventDefault();
    setFocusedIndex(index);
    openMenu(version, event.clientX, event.clientY);
  };
  const rootClassName = ["nk-review-timeline", "nk-review-timeline--vertical", className].filter(Boolean).join(" ");
  if (!hasNavigableTimeline(versions)) return null;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "ul",
      {
        className: rootClassName,
        "data-testid": testId ?? "timeline-bar",
        role: "listbox",
        "aria-label": "Version history",
        "aria-orientation": "vertical",
        children: ordered.map((version, index) => {
          const hasComments = (version.commentCount ?? 0) > 0;
          const isA = version.versionId === selectedA;
          const isB = version.versionId === selectedB;
          const isSelected = isA || isB;
          const isCurrent = version.versionId === currentTickId;
          const revertNote = version.revertOf ? version.revertOf.label ? `revert of ${version.revertOf.label}` : "revert" : null;
          const tickClassName = [
            "nk-review-timeline__tick",
            version.isHead && "nk-review-timeline__tick--head",
            version.stale && "nk-review-timeline__tick--stale",
            hasComments && "nk-review-timeline__tick--has-comments",
            revertNote && "nk-review-timeline__tick--revert",
            isA && "nk-review-timeline__tick--selected-a",
            isB && "nk-review-timeline__tick--selected-b"
          ].filter(Boolean).join(" ");
          const accessibleName = [
            version.label,
            version.isHead && "head",
            isCurrent && "showing",
            revertNote,
            isA && "compare A",
            isB && "compare B",
            version.stale && "stale",
            hasComments && `${version.commentCount} comments`
          ].filter(Boolean).join(", ");
          return /* @__PURE__ */ jsxs(
            "li",
            {
              ref: (el) => {
                itemRefs.current[index] = el;
              },
              role: "option",
              "aria-selected": isSelected,
              "aria-current": isCurrent ? "true" : void 0,
              "aria-label": accessibleName,
              "aria-haspopup": hasActions ? "menu" : void 0,
              "data-menu-open": hasActions ? menu?.versionId === version.versionId ? "true" : "false" : void 0,
              "data-testid": `timeline-tick-${version.seq}`,
              "data-stale": version.stale ? "true" : void 0,
              "data-revert": revertNote ? "true" : void 0,
              tabIndex: index === activeIndex ? 0 : -1,
              className: tickClassName,
              onClick: (event) => handleClick(event, index, version),
              onContextMenu: (event) => handleContextMenu(event, index, version),
              onKeyDown: (event) => handleKeyDown(event, index, version),
              onFocus: () => setFocusedIndex(index),
              children: [
                /* @__PURE__ */ jsx("span", { className: "nk-review-timeline__tick-dot", "aria-hidden": "true" }),
                hasComments ? /* @__PURE__ */ jsx("span", { className: "nk-review-timeline__tick-comments", "aria-hidden": "true", children: version.commentCount }) : null,
                /* @__PURE__ */ jsxs("span", { className: "nk-review-timeline__tick-popover", "aria-hidden": "true", children: [
                  /* @__PURE__ */ jsx("span", { className: "nk-review-timeline__tick-popover-label", children: version.label }),
                  /* @__PURE__ */ jsx("span", { className: "nk-review-timeline__tick-popover-time", children: version.createdAt }),
                  revertNote ? /* @__PURE__ */ jsx("span", { className: "nk-review-timeline__tick-popover-revert", children: revertNote }) : null,
                  hasActions ? /* @__PURE__ */ jsx("span", { className: "nk-review-timeline__tick-popover-hint", children: "Right-click or Shift+F10 for actions" }) : null
                ] })
              ]
            },
            version.versionId
          );
        })
      }
    ),
    menu && menuTick && tickActions ? /* @__PURE__ */ jsx(
      TimelineTickMenu,
      {
        tick: menuTick,
        actions: tickActions,
        anchor: { x: menu.x, y: menu.y },
        onClose: closeMenu
      }
    ) : null
  ] });
}
function useDiffParam(param = "diff") {
  const [token, setTokenState] = useState(
    () => typeof window === "undefined" ? null : readDiffToken(window.location.search, param)
  );
  const setToken = useCallback(
    (next) => {
      setTokenState(next);
      syncDiffTokenToLocation(next, param);
    },
    [param]
  );
  const clear = useCallback(() => {
    setTokenState(null);
    if (typeof window === "undefined" || typeof window.history?.replaceState !== "function") {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.delete(param);
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", url);
  }, [param]);
  return useMemo(() => ({ token, setToken, clear }), [token, setToken, clear]);
}

// src/version.ts
var LIB_VERSION = "0.5.0";
var PANE_PREFIX = "pane:";
var GUTTER_PREFIX = "gutter:";
function slotKind(slot) {
  return slot.slice(PANE_PREFIX.length);
}
function WorkbenchStage(props) {
  const {
    shown,
    renderPane,
    renderGutter,
    gutterTrack,
    paneTrack,
    className,
    testId = "workbench-stage"
  } = props;
  const { gridTemplateColumns, slots } = stageColumns(shown, { gutterTrack, paneTrack });
  const rootClassName = className ? `nk-review-workbench ${className}` : "nk-review-workbench";
  return /* @__PURE__ */ jsx("div", { className: rootClassName, style: { gridTemplateColumns }, "data-testid": testId, children: slots.map((slot) => {
    if (slot.startsWith(PANE_PREFIX)) {
      const kind = slotKind(slot);
      return /* @__PURE__ */ jsx(
        "div",
        {
          className: "nk-review-workbench__column",
          "data-kind": kind,
          "data-slot": slot,
          children: renderPane(kind)
        },
        slot
      );
    }
    const pairing = slot.slice(GUTTER_PREFIX.length);
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: "nk-review-workbench__gutter",
        "data-pairing": pairing,
        "data-slot": slot,
        children: renderGutter?.(pairing)
      },
      slot
    );
  }) });
}

export { CopyLinkButton, DEFAULT_GUTTER_TRACK, DEFAULT_PANE_TRACK, DEFAULT_PILL_OPTIONS, DiffHeader, LIB_VERSION, PillToggleGroup, SideBySideDiff, TIMELINE_MIN_VERSIONS, TimelineBar, TimelineTickMenu, VersionSwitcher, WorkbenchStage, buildDiffPermalink, documentDiffToVersionDiff, hasNavigableTimeline, lineDiffToVersionDiff, parseDiffLink, readDiffToken, refusalFor, serializeDiffLink, stageColumns, syncDiffTokenToLocation, useDiffParam, writeDiffToken };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map